import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Download, FileSpreadsheet, Calendar, AlertTriangle } from "lucide-react";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";

const QUICK_RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "fy", label: "Current FY" },
];

function applyQuickRange(key) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "today") return { start: iso(today), end: iso(today) };
  if (key === "7d") {
    const s = new Date(today); s.setDate(s.getDate() - 6);
    return { start: iso(s), end: iso(today) };
  }
  if (key === "30d") {
    const s = new Date(today); s.setDate(s.getDate() - 29);
    return { start: iso(s), end: iso(today) };
  }
  if (key === "fy") {
    // Indian FY April–March
    const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return { start: `${y}-04-01`, end: iso(today) };
  }
  return { start: "", end: "" };
}

export default function Reports() {
  const [types, setTypes] = useState([]);
  const [reportType, setReportType] = useState("invoices");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // ----- Ageing Outstanding (point-in-time) state -----
  const todayIso = new Date().toISOString().slice(0, 10);
  const [ageAsOf, setAgeAsOf] = useState(todayIso);
  const [ageProject, setAgeProject] = useState("");
  const [ageClient, setAgeClient] = useState("");
  const [ageFy, setAgeFy] = useState("");
  const [ageInfo, setAgeInfo] = useState(null);
  const [ageLoading, setAgeLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    api.get("/projects", { params: { active_only: true } })
      .then(({ data }) => setProjectOptions(data || []))
      .catch(() => setProjectOptions([]));
  }, []);

  const fetchAgeingCount = useCallback(async () => {
    if (!ageAsOf) { setAgeInfo(null); return; }
    try {
      const params = { as_of: ageAsOf };
      if (ageProject) params.project = ageProject;
      if (ageClient.trim()) params.party_client_name = ageClient.trim();
      if (ageFy.trim()) params.fy = ageFy.trim();
      const { data } = await api.get("/ageing-report/count", { params });
      setAgeInfo(data);
    } catch (e) {
      setAgeInfo({ count: 0, err: e.response?.data?.detail || "Could not fetch count" });
    }
  }, [ageAsOf, ageProject, ageClient, ageFy]);

  useEffect(() => { fetchAgeingCount(); }, [fetchAgeingCount]);

  const downloadAgeing = async () => {
    if (!ageAsOf) return toast.error("Pick an As-of date");
    try {
      setAgeLoading(true);
      const params = { as_of: ageAsOf };
      if (ageProject) params.project = ageProject;
      if (ageClient.trim()) params.party_client_name = ageClient.trim();
      if (ageFy.trim()) params.fy = ageFy.trim();
      const res = await api.get("/ageing-report/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ageing_outstanding_${ageAsOf}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ageing report downloaded");
    } catch (e) {
      let msg = "Download failed";
      if (e.response?.data instanceof Blob) {
        try { const j = JSON.parse(await e.response.data.text()); msg = j.detail || msg; } catch (_) {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setAgeLoading(false);
    }
  };

  useEffect(() => {
    api.get("/reports/types").then(({ data }) => setTypes(data)).catch(() => setTypes([]));
    // default to last 30 days
    const r = applyQuickRange("30d");
    setStart(r.start);
    setEnd(r.end);
  }, []);

  const fetchCount = useCallback(async () => {
    if (!start || !end || !reportType) { setInfo(null); return; }
    if (start > end) { setInfo({ count: 0, max: 3000, err: "Start date must be before end date." }); return; }
    try {
      const { data } = await api.get(`/reports/${reportType}/count`, { params: { start_date: start, end_date: end } });
      setInfo(data);
    } catch (e) {
      setInfo({ count: 0, max: 3000, err: e.response?.data?.detail || "Could not fetch count" });
    }
  }, [reportType, start, end]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const download = async () => {
    if (!start || !end) return toast.error("Pick a date range");
    if (start > end) return toast.error("Start date must be before end date");
    try {
      setLoading(true);
      const res = await api.get(`/reports/${reportType}/export`, {
        params: { start_date: start, end_date: end },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${reportType}_${start}_to_${end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      let msg = "Download failed";
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch (_) {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const setQuick = (k) => {
    const r = applyQuickRange(k);
    setStart(r.start);
    setEnd(r.end);
  };

  const tooMany = info && info.count > info.max;
  const noData = info && info.count === 0 && !info.err;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1100px]">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Exports</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Reports</h1>
        <p className="text-sm text-slate-500 mt-2">
          Extract activity data as Excel. Filter is based on the invoice upload date. Maximum 3000 rows per fetch — narrow the date range if needed.
        </p>
      </div>

      <div className="bg-white border border-slate-200 p-6 space-y-6">
        {/* Report type */}
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Report Type</Label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="rounded-none mt-1 max-w-md" data-testid="report-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Date range */}
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Date Range (Invoice Upload Date)</Label>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="rounded-none pl-9 w-52"
                data-testid="report-start"
              />
            </div>
            <span className="text-xs text-slate-400">to</span>
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="rounded-none pl-9 w-52"
                data-testid="report-end"
              />
            </div>
            <div className="flex gap-2 ml-2">
              {QUICK_RANGES.map((q) => (
                <button
                  key={q.key}
                  onClick={() => setQuick(q.key)}
                  className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:text-[#002FA7] border border-slate-200 px-2.5 py-1.5"
                  data-testid={`report-quick-${q.key}`}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Info banner */}
        {info && (
          <div
            className={`text-xs px-3 py-2 border ${
              info.err
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : tooMany
                ? "bg-red-50 border-red-200 text-red-800"
                : noData
                ? "bg-slate-50 border-slate-200 text-slate-600"
                : "bg-emerald-50 border-emerald-200 text-emerald-800"
            }`}
            data-testid="report-info"
          >
            {info.err
              ? info.err
              : tooMany
              ? `${info.count} rows match — exceeds the ${info.max}-row limit. Please choose a smaller date range.`
              : noData
              ? "No records found for the selected range."
              : `${info.count} of max ${info.max} rows will be included in this export.`}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
          <Button
            onClick={download}
            disabled={loading || !info || info.err || tooMany || info.count === 0}
            className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10"
            data-testid="report-download"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {loading ? "Generating…" : "Download Excel"}
          </Button>
          <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5" />
            One sheet per report · headers in first row
          </div>
        </div>
      </div>

      {/* ============= Ageing Outstanding Report ============= */}
      <div className="mt-10" data-testid="ageing-report-section">
        <div className="mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-700">Point-in-Time Snapshot</div>
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mb-2">Ageing Outstanding Report</h2>
        <p className="text-sm text-slate-500 mb-4">
          Picks every invoice that has a non-zero outstanding balance and buckets it by how old the payment is, as of the chosen date. Excel includes a Summary sheet (per bucket) and a Detail sheet (per invoice with payment, retention, days overdue and status).
        </p>

        <div className="bg-white border border-slate-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">As-of Date</Label>
              <div className="relative mt-1">
                <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={ageAsOf}
                  onChange={(e) => setAgeAsOf(e.target.value)}
                  className="rounded-none pl-9"
                  data-testid="ageing-as-of"
                />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">Defaults to today.</div>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Project (optional)</Label>
              <Select value={ageProject || "__all__"} onValueChange={(v) => setAgeProject(v === "__all__" ? "" : v)}>
                <SelectTrigger className="rounded-none mt-1" data-testid="ageing-project">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Projects</SelectItem>
                  {projectOptions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Client / Party (optional)</Label>
              <Input
                value={ageClient}
                onChange={(e) => setAgeClient(e.target.value)}
                placeholder="Exact client name"
                className="rounded-none mt-1"
                data-testid="ageing-client"
              />
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">FY (optional)</Label>
              <Input
                value={ageFy}
                onChange={(e) => setAgeFy(e.target.value)}
                placeholder="e.g. 2026-27"
                className="rounded-none mt-1"
                data-testid="ageing-fy"
              />
            </div>
          </div>

          {/* Info banner */}
          {ageInfo && (
            <div
              className={`text-xs px-3 py-2 border ${
                ageInfo.err
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : ageInfo.count === 0
                  ? "bg-slate-50 border-slate-200 text-slate-600"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800"
              }`}
              data-testid="ageing-info"
            >
              {ageInfo.err
                ? ageInfo.err
                : ageInfo.count === 0
                ? `No outstanding invoices as of ${ageInfo.as_of}.`
                : <>{ageInfo.count} outstanding invoice{ageInfo.count === 1 ? "" : "s"} · Total <strong>{formatINR(ageInfo.total_outstanding || 0)}</strong> as of {ageInfo.as_of}</>}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <Button
              onClick={downloadAgeing}
              disabled={ageLoading || !ageAsOf || !ageInfo || ageInfo.err || ageInfo.count === 0}
              className="bg-amber-600 hover:bg-amber-700 rounded-none h-10"
              data-testid="ageing-download"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {ageLoading ? "Generating…" : "Download Ageing Excel"}
            </Button>
            <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <FileSpreadsheet className="w-3.5 h-3.5" />
              2 sheets · Summary + Invoice Detail
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
